import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useAuth } from '@/hooks/use-auth'
import { splitAddressDisplay } from '@/lib/address'
import { deleteBiddleDraft, listBiddleDrafts, stashDraftForResume } from '@/lib/biddle-drafts'
import { openBiddleDetail } from '@/lib/biddle-navigation'
import { callableErrorMessage } from '@/lib/callable-errors'
import { db } from '@/lib/firebase'
import { openCreateBiddle } from '@/lib/property-navigation'
import { formatAud, relativeTime } from '@/lib/utils'
import {
  biddleStatus,
  isBiddleActive,
  parseBiddle,
  parseProperty,
  type Biddle,
  type BiddleDraft,
  type Property,
} from '@/types/models'

type Tab = 'active' | 'past' | 'drafts'

type Row = { biddle: Biddle; property: Property | null }

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

function draftPriceLabel(price: string): string | null {
  const n = Number(String(price).replace(/[^\d.]/g, ''))
  if (!Number.isFinite(n) || n <= 0) return null
  return formatAud(Math.round(n))
}

export function BiddlesPage() {
  return (
    <RequireAuth>
      <BiddlesList />
    </RequireAuth>
  )
}

function BiddlesList() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('active')
  const [rows, setRows] = useState<Row[]>([])
  const [drafts, setDrafts] = useState<BiddleDraft[]>([])
  const [loading, setLoading] = useState(true)
  const [draftsLoading, setDraftsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const tagsKey = (profile?.sentBiddles ?? []).join('|')

  useEffect(() => {
    const tags = tagsKey ? tagsKey.split('|') : []
    let cancelled = false
    ;(async () => {
      if (tags.length === 0) {
        setRows([])
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const biddles: Biddle[] = []
        for (const part of chunk(tags, 30)) {
          const q = query(
            collection(db, 'biddles'),
            where('buyerBiddleTag', 'in', part),
            where('isDeleted', '==', false),
          )
          const snap = await getDocs(q)
          snap.docs.forEach((d: QueryDocumentSnapshot) => {
            biddles.push(parseBiddle(d.id, d.data() as Record<string, unknown>))
          })
        }

        const propertyIds = [...new Set(biddles.map((b) => b.propertyID).filter(Boolean))]
        const propertyMap = new Map<string, Property>()
        await Promise.all(
          propertyIds.map(async (id) => {
            const snap = await getDoc(doc(db, 'properties', id))
            if (snap.exists()) {
              propertyMap.set(id, parseProperty(snap.id, snap.data() as Record<string, unknown>))
            }
          }),
        )

        if (cancelled) return
        const next = biddles
          .map((biddle) => ({
            biddle,
            property: propertyMap.get(biddle.propertyID) ?? null,
          }))
          .sort((a, b) => b.biddle.timeCreated.getTime() - a.biddle.timeCreated.getTime())
        setRows(next)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load Biddles')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tagsKey])

  useEffect(() => {
    if (tab !== 'drafts' || !user) return
    let cancelled = false
    ;(async () => {
      setDraftsLoading(true)
      setDraftError(null)
      try {
        const list = await listBiddleDrafts(user.uid)
        if (!cancelled) setDrafts(list)
      } catch (e) {
        if (!cancelled) setDraftError(callableErrorMessage(e))
      } finally {
        if (!cancelled) setDraftsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tab, user])

  const filtered = useMemo(() => {
    return rows.filter(({ biddle }) =>
      tab === 'active' ? isBiddleActive(biddle) : !isBiddleActive(biddle),
    )
  }, [rows, tab])

  function openDraft(draft: BiddleDraft) {
    stashDraftForResume(draft)
    openCreateBiddle(navigate, draft.propertyId, { pathname: '/biddles' })
  }

  async function onDeleteDraft(draft: BiddleDraft) {
    if (!user) return
    setDeletingId(draft.propertyId)
    setDraftError(null)
    try {
      await deleteBiddleDraft(user.uid, draft.propertyId)
      setDrafts((prev) => prev.filter((d) => d.propertyId !== draft.propertyId))
    } catch (e) {
      setDraftError(callableErrorMessage(e))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-navy">Your Biddles</h1>
        <p className="mt-1 text-sm text-muted">Expressions of interest you’ve sent to owners.</p>
      </div>

      <div className="flex gap-2 rounded-xl bg-surface p-1">
        {([
          { key: 'active', label: 'Active' },
          { key: 'past', label: 'Past' },
          { key: 'drafts', label: 'Drafts' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={
              tab === key
                ? 'flex-1 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-navy shadow-sm'
                : 'flex-1 rounded-lg px-3 py-2 text-sm font-medium text-muted'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'drafts' ? (
        draftsLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : draftError ? (
          <p className="text-sm text-danger">{draftError}</p>
        ) : drafts.length === 0 ? (
          <EmptyState
            title="No draft Biddles"
            description="Start a Biddle on a property, then tap Save draft to come back later."
            actionLabel="Search properties"
            onAction={() => navigate('/search')}
          />
        ) : (
          <ul className="space-y-3">
            {drafts.map((draft) => {
              const { street, locality } = splitAddressDisplay(draft.fullAddress)
              const priceLabel = draftPriceLabel(draft.price)
              return (
                <li key={draft.propertyId}>
                  <Card className="transition hover:border-biddle-blue">
                    <CardContent className="flex items-center gap-3 pt-5">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => openDraft(draft)}
                      >
                        <p className="font-semibold text-navy">{street || 'Property'}</p>
                        {locality ? (
                          <p className="mt-0.5 text-sm text-muted">{locality}</p>
                        ) : null}
                        <p className="mt-1 text-sm text-muted">
                          {priceLabel ? `${priceLabel} · ` : null}
                          Saved {relativeTime(draft.updatedAt)}
                        </p>
                      </button>
                      <Badge tone="Pending">Draft</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={deletingId === draft.propertyId}
                        aria-label="Delete draft"
                        onClick={() => void onDeleteDraft(draft)}
                      >
                        {deletingId === draft.propertyId ? (
                          <Spinner className="h-4 w-4" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-muted" />
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </li>
              )
            })}
          </ul>
        )
      ) : loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={tab === 'active' ? 'No active Biddles' : 'No past Biddles'}
          description={
            tab === 'active'
              ? 'Search for a property and create your first Biddle.'
              : 'Expired or withdrawn Biddles will show up here.'
          }
          actionLabel="Search properties"
          onAction={() => navigate('/search')}
        />
      ) : (
        <ul className="space-y-3">
          {filtered.map(({ biddle, property }) => {
            const status = biddleStatus(biddle)
            const { street, locality } = property
              ? splitAddressDisplay(property.fullAddress)
              : { street: '', locality: '' }
            return (
              <li key={biddle.id}>
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => openBiddleDetail(navigate, biddle.propertyID, biddle.id)}
                >
                  <Card className="transition hover:border-biddle-blue">
                    <CardContent className="flex items-center justify-between gap-3 pt-5">
                      <div className="min-w-0">
                        <p className="font-semibold text-navy">{street || 'Property'}</p>
                        {locality ? (
                          <p className="mt-0.5 text-sm text-muted">{locality}</p>
                        ) : null}
                        <p className="mt-1 text-sm text-muted">
                          {formatAud(biddle.price)} · {relativeTime(biddle.timeCreated)}
                        </p>
                      </div>
                      <Badge tone={status}>{status}</Badge>
                    </CardContent>
                  </Card>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
